import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import fp from 'lodash/fp';
import { placeholder } from 'polished';
import { connect } from 'react-redux';
import { isBrowser } from 'react-device-detect';
import {
  ButtonBase as Button,
  Icon as FaIcon,
} from '@deerplusofficial/pos-components';
import { color, media, size, mapState } from '@deerplusofficial/pos-helper';
import actions from '../../actions';
import { withQueryUser } from '../../model/Queries';
import { userShape } from '../../model/Types';

const Container = styled.div`
  background-color: ${color.background.dark};
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  height: ${size.topbar.mobile};
  ${props =>
    props.hideOnMobile &&
    css`
      display: none;
      ${media(
        'tablet',
        css`
          display: flex;
        `
      )};
    `};
  ${media(
    'tablet',
    css`
      height: ${size.topbar.tablet};
    `
  )};
`;

const MenuToggle = styled(Button)`
  padding: 0 ${size.productGap};
  color: white;
  ${media(
    'tablet',
    css`
      padding: 0 ${size.padding.x};
    `
  )};
`;

const DetailToggle = MenuToggle.extend`
  margin-left: auto;
  color: #ffffff;
`;

const Icon = styled(FaIcon)`
  color: currentColor;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  ${media(
    'tablet',
    css`
      margin-left: ${size.padding.x};
      flex-grow: 0;
    `
  )};
`;

const SearchBarToggle = styled(Button)`
  display: flex;
  align-items: center;
  padding: 0 ${size.productGap};
  margin-left: auto;
  order: 1;
  color: white;
  ${media(
    'tablet',
    css`
      order: 0;
      ${props =>
        props.disabled &&
        css`
          pointer-events: none;
        `};
    `
  )};
`;

const SearchBarInput = styled.input.attrs({ type: 'search' })`
  display: block;
  flex-grow: 1;
  padding: 5px 8px;
  appearance: none;
  border: none;
  outline: 0;
  box-shadow: none;
  background-color: rgba(255, 255, 255, 0.3);
  color: #ffffff;
  ${placeholder({ color: '#ffffff' })};
  ${props =>
    props.hide &&
    css`
      visibility: hidden;
    `}
`;

const SearchBarInputPlaceholder = styled.div`
  display: none;
  margin-left: ${size.productGap};
  color: #ffffff;
  ${media(
    'tablet',
    css`
      display: block;
    `
  )};
`;

const ScannerToggle = styled(Button)`
  display: flex;
  align-items: center;
  color: ${props => (props.active ? color.accent : 'white')};
`;

const ScannerLabel = styled.div`
  margin-left: 16px;
`;

const Avatar = styled.div`
  display: none;
  ${media(
    'tablet',
    css`
      display: flex;
      align-items: center;
      margin-left: auto;
      margin-right: ${size.padding.x};
      font-size: 20px;
      color: #ffffff;
    `
  )};
`;

const Photo = styled.img.attrs({ width: 32, height: 32 })`
  ${media(
    'tablet',
    css`
      display: block;
      border-radius: 50%;
      margin-left: 12px;
    `
  )};
`;

class Topbar extends Component {
  static propTypes = {
    user: userShape,
    hasSearch: PropTypes.bool,
    orderDetail: PropTypes.shape(),
    orderStep: PropTypes.string,
    searchKeyword: PropTypes.string,
    editSearchKeyword: PropTypes.func,
    editOrderStep: PropTypes.func,
    toggleDrawer: PropTypes.func,
    openDetail: PropTypes.func,
    closeDetail: PropTypes.func,
  };

  static defaultProps = {
    user: {},
    hasSearch: true,
    orderDetail: {},
    orderStep: 'order',
    searchKeyword: '',
    editSearchKeyword: _.identity,
    editOrderStep: _.identity,
    toggleDrawer: _.identity,
    openDetail: _.identity,
    closeDetail: _.identity,
  };

  state = {
    isOpenSearchBarInput: false,
  };

  componentDidMount() {
    isBrowser && this.onSearchBarToggle();
  }

  onPrevPress = () => this.props.editOrderStep('order');

  onSearchBarToggle = () => {
    const { isOpenSearchBarInput } = this.state;
    this.setState({ isOpenSearchBarInput: !isOpenSearchBarInput });
  };

  onSearchBarInputBlur = () => {
    const { searchKeyword } = this.props;
    _.size(searchKeyword) === 0 &&
      this.setState({ isOpenSearchBarInput: false });
  };

  onSearchKeywordChange = ({ target: { value } }) =>
    this.editSearchKeyword(value);

  editSearchKeyword = _.debounce(
    value => this.props.editSearchKeyword(value),
    150
  );

  get isInitSearchBarInput() {
    const { isOpenSearchBarInput } = this.state;
    const { searchKeyword } = this.props;
    return !isOpenSearchBarInput && _.size(searchKeyword) === 0;
  }

  get isCheckout() {
    return this.props.orderStep === 'checkout';
  }

  get isLineupView() {
    const { orderStep, hasSearch } = this.props;
    return orderStep === 'order' && hasSearch;
  }

  get fullName() {
    const {
      user: { first_name, last_name },
    } = this.props;
    return _.join([first_name, last_name], ' ');
  }

  render() {
    const { isOpenSearchBarInput } = this.state;
    const {
      user,
      orderDetail,
      scanner,
      toggleDetail,
      toggleDrawer,
      toggleScanner,
    } = this.props;

    return (
      <Container>
        <Inner hideOnMobile={this.isCheckout}>
          <MenuToggle onClick={toggleDrawer}>
            <Icon icon="bars" />
          </MenuToggle>
          {this.isLineupView && (
            <SearchBar>
              <ScannerToggle onClick={toggleScanner} active={scanner.active}>
                <Icon light icon="scanner" />
                {scanner.active && <ScannerLabel>已啟用掃碼功能</ScannerLabel>}
              </ScannerToggle>
              {!scanner.active && (
                <React.Fragment>
                  <SearchBarToggle
                    disabled={!this.isInitSearchBarInput}
                    onClick={this.onSearchBarToggle}
                  >
                    <Icon regular icon="search" />
                    {this.isInitSearchBarInput && (
                      <SearchBarInputPlaceholder>
                        輸入商品名稱、條碼
                      </SearchBarInputPlaceholder>
                    )}
                  </SearchBarToggle>
                </React.Fragment>
              )}
              {isOpenSearchBarInput && (
                <SearchBarInput
                  autoFocus
                  hide={scanner.active}
                  onChange={this.onSearchKeywordChange}
                  onBlur={this.onSearchBarInputBlur}
                />
              )}
            </SearchBar>
          )}
          <Avatar>
            {this.fullName}
            {user.avatar && <Photo src={user.avatar} alt={this.fullName} />}
          </Avatar>
        </Inner>
        {this.isCheckout && (
          <Inner className="hide-on-tablet">
            <MenuToggle onClick={this.onPrevPress}>
              <Icon icon="chevron-left" />
            </MenuToggle>
            <DetailToggle onClick={toggleDetail}>
              {orderDetail.active ? <Icon icon="times" /> : '明細'}
            </DetailToggle>
          </Inner>
        )}
      </Container>
    );
  }
}

const { edit: editOrderStep } = actions.orderStep;
const { edit: editSearchKeyword } = actions.searchKeyword;
const { toggle: toggleDetail } = actions.orderDetail;
const { toggle: toggleDrawer } = actions.drawer;
const { toggle: toggleScanner } = actions.scanner;

export default fp.compose(
  withQueryUser(),
  connect(
    mapState(['user', 'orderStep', 'orderDetail', 'searchKeyword', 'scanner']),
    {
      editOrderStep,
      editSearchKeyword,
      toggleDetail,
      toggleDrawer,
      toggleScanner,
    }
  )
)(Topbar);



// WEBPACK FOOTER //
// ./src/components/Topbar/Topbar.js